import { useMemo } from 'react'
import { usePortfolioHouseVisits } from '../../StateManagment/PortfolioHouseVisitsContext'
import {
    aggregateAdressesByProject,
    HOUSEVISITS_STATUSES,
} from '../../helpers/housevisitsPortfolio/aggregateHouseVisits'
import { ADRESS_STATUS_LABELS } from '../../helpers/adressHelpers/adressStatusLabels'
import OutputMultiLineGraph from '../Visualisations/outputs/OutputMultiLineGraph'
import StaticCardLabel from './StaticCardLabel'
import DashboardCardOptions from './DashboardCardOptions'
import CardHeaderInfoPopover from '../common/CardHeaderInfoPopover'
import ProjectContributionBreakdown from './housevisitsPortfolio/ProjectContributionBreakdown'
import DashboardCardEmptyState from './DashboardCardEmptyState'
import { CATEGORICAL } from '../../helpers/chartPalette'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card

const STATUS_COLUMNS = HOUSEVISITS_STATUSES.map((id) => ({
    key: id,
    label: ADRESS_STATUS_LABELS[id],
}))

/**
 * @param {object} props
 * @param {object} props.item - The DashboardItems doc backing this static card
 */
const DashboardPortfolioHouseVisitsStatusCard = ({ item }) => {
    const { projects, adresses, loading } = usePortfolioHouseVisits()
    // Vaste paletkleur. Deze kaart staat alleen op het dashboard, dus er valt
    // niets te kiezen; de opgeslagen ColorOverrides blijven in Firestore staan
    // maar worden niet meer gelezen. Zie docs/patterns.md#ui-chart-palette.
    const barColor = CATEGORICAL[0]

    const adressRows = useMemo(
        () => aggregateAdressesByProject(projects, adresses),
        [projects, adresses]
    )

    const statusTotals = useMemo(() => {
        const totals = HOUSEVISITS_STATUSES.reduce((acc, s) => {
            acc[s] = 0
            return acc
        }, /** @type {Record<string, number>} */ ({}))
        for (const row of adressRows) {
            for (const status of HOUSEVISITS_STATUSES) {
                totals[status] += row.byStatus?.[status] || 0
            }
        }
        return totals
    }, [adressRows])

    const statusData = useMemo(() => (
        HOUSEVISITS_STATUSES.map((id) => ({
            name: ADRESS_STATUS_LABELS[id],
            id,
            'Aantal': { total: statusTotals[id] || 0 },
        }))
    ), [statusTotals])

    const breakdown = useMemo(() => (
        adressRows.map((row) => ({
            CompagnyID: row.CompagnyID,
            name: row.name,
            byColumn: { ...(row.byStatus || {}) },
        }))
    ), [adressRows])

    const bars = [{ Title: 'Aantal', Color: barColor }]

    const popoverContent = (
        <ProjectContributionBreakdown items={breakdown} columns={STATUS_COLUMNS} />
    )

    const isEmpty = !loading && projects.length === 0

    return (
        <div className='housevisits-card-body'>
            <StaticCardLabel
                title='Status huishoudens (portfolio)'
                pill={{ variant: 'housevisits', label: 'Portfolio' }}
            />
            {!isEmpty && (
                <CardHeaderInfoPopover content={popoverContent} label='Bijdrage per project' />
            )}
            <div className='housevisits-card-chart'>
                {loading ? (
                    <p className='portfolio-breakdown-empty'>Data laden…</p>
                ) : isEmpty ? (
                    <DashboardCardEmptyState type='portfolio-housevisits-status' />
                ) : (
                    <OutputMultiLineGraph
                        data={statusData}
                        tableCategoryLabel="Status"
                        graphType={'bar'}
                        outputs={bars}
                        color={barColor}
                        growth={{ average: 0, perOutput: {} }}
                        height={'100%'}
                        displayLegend={false}
                    />
                )}
            </div>
            <DashboardCardOptions item={item} />
        </div>
    )
}

export default DashboardPortfolioHouseVisitsStatusCard
